import { createTheme } from '@mui/material/styles';
import {
  UNKNOWN_DOMAIN,
  JOIST_DOMAIN,
  EVERPRO_DOMAIN,
  getAppDomain,
  SERVICE_NATION_BY_EVER_PRO_DOMAIN,
} from '../config/domains';

// A custom theme to be used in the app
const commonTheme = createTheme({
  palette: {
    effects: {
      gradientBorder: 'rgba(255, 255, 255, 0.12)',
      shadowInnerBorder: 'rgba(10, 13, 18, 0.18)',
      shadowInner: 'rgba(10, 13, 18, 0.05)',
      shadowXs: 'rgba(10, 13, 18, 0.05)',
    },
    brand: {
      primary: '#32302F',
      secondary: '#f9b30c',
      surface: '#FFF5DF',
      accent: '#e8bd5a',
    },
  },
  typography: {
    fontFamily: 'system-ui, sans-serif',
    button: {
      textTransform: 'none',
    },
    body1: {
      fontSize: '16px',
      fontWeight: 400,
    },
    body2: {
      fontSize: '16px',
      fontWeight: 500,
    },
    body3: {
      fontSize: '17px',
      fontWeight: 550,
    },
    body4: {
      fontSize: '18px',
      fontWeight: 700,
    },
    body5: {
      fontSize: '18px',
      fontWeight: 500,
    },
    body6: {
      fontSize: '23px',
      fontWeight: 700,
    },
    body7: {
      fontSize: '14px',
      fontWeight: 500,
    },
    body8: {
      fontSize: '14px',
      fontWeight: 400,
    },
    h2: {
      fontSize: '20px',
      fontWeight: 600,
    },
    h3: {
      fontSize: '20px',
      fontWeight: 400,
    },
    h4: {
      fontSize: '18px',
      fontWeight: 600,
    },
    h5: {
      fontSize: '20px',
      fontWeight: 500,
    },
    h6: {
      fontSize: '14px',
      fontWeight: 600,
    },
    h7: {
      fontSize: '26px',
      fontWeight: 400,
    },
    h8: {
      fontSize: '30px',
      fontWeight: 700,
    },
    h9: {
      fontSize: '22px',
      fontWeight: 600,
    },
    h10: {
      fontSize: '12px',
      fontWeight: 500,
    },
    h11: {
      fontSize: '24px',
      fontWeight: 700,
    },
    h12: {
      fontSize: '24px',
      fontWeight: 600,
    },
    h13: {
      fontSize: '12px',
      fontWeight: 400,
    },
    h14: {
      fontSize: '36px',
      fontWeight: 700,
    },
    h15: {
      fontSize: '16px',
      fontWeight: 600,
    },
    h16: {
      fontSize: '14px',
      fontWeight: 700,
    },
    headingXl: {
      fontSize: '32px',
      fontWeight: 700,
      lineHeight: '40px',
      letterSpacing: '-0.4px',
      fontFamily: 'open-sans, sans-serif',
    },
    headingLg: {
      fontSize: '28px',
      fontWeight: 700,
      lineHeight: '36px',
      letterSpacing: '-0.4px',
      fontFamily: 'open-sans, sans-serif',
    },
    headingMd: {
      fontSize: '24px',
      fontWeight: 700,
      lineHeight: '32px',
      letterSpacing: '-0.4px',
      fontFamily: 'open-sans, sans-serif',
    },
    headingSm: {
      fontSize: '20px',
      fontWeight: 700,
      lineHeight: '28px',
      letterSpacing: '0px',
      fontFamily: 'open-sans, sans-serif',
    },
    headingXs: {
      fontSize: '16px',
      fontWeight: 700,
      lineHeight: '24px',
      letterSpacing: '0px',
      fontFamily: 'open-sans, sans-serif',
    },
    headingXxs: {
      fontSize: '14px',
      fontWeight: 700,
      lineHeight: '20px',
      letterSpacing: '0px',
      fontFamily: 'open-sans, sans-serif',
    },
    bodyLg: {
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: '24px',
      letterSpacing: '0px',
      fontFamily: 'roboto, sans-serif',
    },
    bodyLgProminent: {
      fontSize: '16px',
      fontWeight: 600,
      lineHeight: '24px',
      letterSpacing: '0px',
      fontFamily: 'roboto, sans-serif',
    },
    bodyLgUnderline: {
      fontSize: '16px',
      fontWeight: 600,
      lineHeight: '24px',
      letterSpacing: '0px',
      textDecoration: 'underline',
      fontFamily: 'roboto, sans-serif',
    },
    bodyMd: {
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '20px',
      letterSpacing: '0px',
      fontFamily: 'roboto, sans-serif',
    },
    bodyMdProminent: {
      fontSize: '14px',
      fontWeight: 600,
      lineHeight: '20px',
      letterSpacing: '0px',
      fontFamily: 'roboto, sans-serif',
    },
    bodyMdItalic: {
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '20px',
      letterSpacing: '0px',
      fontStyle: 'italic',
      fontFamily: 'roboto, sans-serif',
    },
    bodyMdUnderline: {
      fontSize: '14px',
      fontWeight: 600,
      lineHeight: '20px',
      letterSpacing: '0px',
      textDecoration: 'underline',
      fontFamily: 'roboto, sans-serif',
    },
    labelLg: {
      fontSize: '16px',
      fontWeight: 600,
      lineHeight: '24px',
      letterSpacing: '0px',
      fontFamily: 'inter, sans-serif',
    },
    labelLgProminent: {
      fontSize: '16px',
      fontWeight: 700,
      lineHeight: '24px',
      letterSpacing: '0.5px',
      fontFamily: 'inter, sans-serif',
    },
    labelMd: {
      fontSize: '14px',
      fontWeight: 600,
      lineHeight: '20px',
      letterSpacing: '0px',
      fontFamily: 'inter, sans-serif',
    },
    labelMdProminent: {
      fontSize: '14px',
      fontWeight: 700,
      lineHeight: '20px',
      letterSpacing: '0.5px',
      fontFamily: 'inter, sans-serif',
    },
    labelSm: {
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: '16px',
      letterSpacing: '0px',
      fontFamily: 'inter, sans-serif',
    },
    labelSmProminent: {
      fontSize: '12px',
      fontWeight: 600,
      lineHeight: '16px',
      letterSpacing: '0px',
      fontFamily: 'inter, sans-serif',
    },
    labelSmItalic: {
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: '16px',
      letterSpacing: '0px',
      fontStyle: 'italic',
      fontFamily: 'inter, sans-serif',
    },
  },
  spacing: 8,
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderBottomRightRadius: 8,
          borderBottomLeftRadius: 8,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          textAlign: 'left',
          borderColor: '#fff',
          borderTopRightRadius: 8,
          borderTopLeftRadius: 8,
          backgroundColor: '#fff',
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          body3: 'p',
          body4: 'p',
          body5: 'p',
          h9: 'p',
          h10: 'p',
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: false,
      },
    },
    MuiButton: {
      styleOverrides: {
        contained: () => ({
          backgroundSize: '100% 100%',
          backgroundRepeat: 'no-repeat',
          transition: 'background-image 0.15s ease',
          '&:hover': {
            backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.1))',
          },
          '&:active': {
            backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
          },
        }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundSize: '100% 100%',
          backgroundRepeat: 'no-repeat',
          transition: 'background-image 0.15s ease',
          '&:hover': {
            backgroundImage: `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover})`,
          },
          '&:active': {
            backgroundImage: `linear-gradient(${theme.palette.action.selected}, ${theme.palette.action.selected})`,
          },
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundSize: '100% 100%',
          backgroundRepeat: 'no-repeat',
          transition: 'background-image 0.15s ease',
          '&:hover': {
            backgroundImage: `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover})`,
          },
          '&:active': {
            backgroundImage: `linear-gradient(${theme.palette.action.selected}, ${theme.palette.action.selected})`,
          },
        }),
      },
    },
    MuiCssBaseline: {
      styleOverrides: (theme) => ({
        a: {
          color: theme.palette.brand.primary,
          textDecoration: 'underline',
          transition: 'color 0.1s ease',
          '&:hover': {
            color: `color-mix(in srgb, ${theme.palette.brand.primary}, black 35%)`,
          },
          '&:active': {
            color: `color-mix(in srgb, ${theme.palette.brand.primary}, black 50%)`,
            transition: 'none',
          },
          '&:visited': {
            color: `color-mix(in srgb, ${theme.palette.brand.primary}, black 25%)`,
          },
        },
      }),
    },
    MuiLink: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.brand.primary,
          textDecoration: 'underline',
          transition: 'color 0.1s ease',
          '&:hover': {
            color: `color-mix(in srgb, ${theme.palette.brand.primary}, black 35%)`,
          },
          '&:active': {
            color: `color-mix(in srgb, ${theme.palette.brand.primary}, black 50%)`,
            transition: 'none',
          },
          '&:visited': {
            color: `color-mix(in srgb, ${theme.palette.brand.primary}, black 25%)`,
          },
        }),
      },
    },
  },
});

export const joistTheme = createTheme(commonTheme, {
  // Add joist specific themes here
  palette: {
    brand: {
      primary: '#007300',
      secondary: '#232323',
      surface: '#EFFAEF',
      accent: '#484ADF',
    },
  },
});

export const everProTheme = createTheme(commonTheme, {
  // Add everPro specific theme here
  palette: {
    brand: {
      primary: '#32302F',
      secondary: '#f9b30c',
      surface: '#FFFAEE',
      accent: '#E8BD5A',
    },
  },
});

export const snByEverProTheme = createTheme(commonTheme, {
  // Add Service Nation by Ever Pro specific theme here
  palette: {
    brand: {
      primary: '#517189',
      secondary: '#569bb4',
      surface: '#F4F8FB',
      accent: '#002A4E',
    },
  },
});

const domainThemes = {
  [JOIST_DOMAIN]: joistTheme,
  [EVERPRO_DOMAIN]: everProTheme,
  [SERVICE_NATION_BY_EVER_PRO_DOMAIN]: snByEverProTheme,
  // add all themes here as comma separated. Order matters here since it's doing a deep merge
  [UNKNOWN_DOMAIN]: commonTheme,
};

// determine current domain of app
const appDomain = getAppDomain();
export default domainThemes[appDomain];
